// src/components/Admin/AdminRaffle.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { raffleService } from '../../services/raffleService';
import './AdminRaffle.css';

const AdminRaffle = ({ user, addNotification }) => {
    const [activeTab, setActiveTab] = useState('events');
    const [events, setEvents] = useState([]);
    const [payments, setPayments] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDrawModal, setShowDrawModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState({});
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    
    // Form states
    const [newEvent, setNewEvent] = useState({
        title: '',
        description: '',
        ticket_price: 500,
        max_tickets_per_member: 50,
        total_tickets: 1000,
        draw_date: '',
        prizes: []
    });
    
    const [newPrize, setNewPrize] = useState({
        name: '',
        description: '',
        value: 0,
        position: 1
    });

    // Load data
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [eventsRes, paymentsRes, settingsRes] = await Promise.all([
                raffleService.adminGetEvents(),
                raffleService.adminGetPayments('pending'),
                raffleService.getSettings()
            ]);
            
            if (eventsRes.success) setEvents(eventsRes.events || []);
            if (paymentsRes.success) setPayments(paymentsRes.payments || []);
            if (settingsRes.success) setSettings(settingsRes.settings);
            
        } catch (error) {
            console.error('Load admin data error:', error);
            addNotification('Failed to load raffle data', 'error');
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Handle create event
    const handleCreateEvent = async () => {
        if (!newEvent.title || !newEvent.draw_date) {
            addNotification('Please fill in all required fields', 'warning');
            return;
        }
        
        try {
            const response = await raffleService.createRaffleEvent(newEvent);
            if (response.success) {
                addNotification('Raffle event created successfully!', 'success');
                setShowCreateModal(false);
                setNewEvent({
                    title: '',
                    description: '',
                    ticket_price: 500,
                    max_tickets_per_member: 50,
                    total_tickets: 1000,
                    draw_date: '',
                    prizes: []
                });
                loadData();
            } else {
                addNotification(response.message || 'Failed to create event', 'error');
            }
        } catch (error) {
            addNotification('Failed to create event', 'error');
        }
    };

    // Handle add prize
    const handleAddPrize = () => {
        if (!newPrize.name) {
            addNotification('Prize name required', 'warning');
            return;
        }
        
        setNewEvent({
            ...newEvent,
            prizes: [...newEvent.prizes, { ...newPrize, id: Date.now() }]
        });
        setNewPrize({
            name: '',
            description: '',
            value: 0,
            position: newEvent.prizes.length + 2
        });
    };

    // Handle remove prize
    const handleRemovePrize = (prizeId) => {
        setNewEvent({
            ...newEvent,
            prizes: newEvent.prizes.filter(p => p.id !== prizeId)
        });
    };

    // Handle verify payment
    const handleVerifyPayment = async (paymentId, action) => {
        let rejectionReason = '';
        if (action === 'reject') {
            rejectionReason = prompt('Enter reason for rejection:');
            if (!rejectionReason) return;
        }
        
        try {
            const response = await raffleService.adminVerifyPayment(paymentId, action, rejectionReason);
            if (response.success) {
                addNotification(response.message, 'success');
                loadData();
            } else {
                addNotification(response.message || 'Verification failed', 'error');
            }
        } catch (error) {
            addNotification('Failed to verify payment', 'error');
        }
    };

    // Handle run draw
    const handleRunDraw = async () => {
        if (!selectedEvent) return;
        
        const confirm = window.confirm(`Are you sure you want to run the draw for "${selectedEvent.title}"? This action cannot be undone.`);
        if (!confirm) return;
        
        try {
            const response = await raffleService.adminRunDraw(selectedEvent.id);
            if (response.success) {
                addNotification(`Draw completed! ${response.total_winners} winners selected.`, 'success');
                setShowDrawModal(false);
                setSelectedEvent(null);
                loadData();
            } else {
                addNotification(response.message || 'Draw failed', 'error');
            }
        } catch (error) {
            addNotification('Failed to run draw', 'error');
        }
    };

    // Handle update settings
    const handleUpdateSettings = async () => {
        try {
            const response = await raffleService.adminUpdateSettings(settings);
            if (response.success) {
                addNotification('Settings updated successfully!', 'success');
                setShowSettingsModal(false);
            } else {
                addNotification(response.message || 'Failed to update settings', 'error');
            }
        } catch (error) {
            addNotification('Failed to update settings', 'error');
        }
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Handle view event
    const handleViewEvent = (event) => {
        setSelectedEvent(event);
        setShowViewModal(true);
    };

    // Stats cards
    const stats = {
        total_events: events.length,
        active_events: events.filter(e => e.draw_status === 'upcoming').length,
        completed_events: events.filter(e => e.draw_status === 'completed').length,
        pending_payments: payments.length,
        total_tickets_sold: events.reduce((sum, e) => sum + (e.tickets_sold_count || 0), 0),
        total_revenue: events.reduce((sum, e) => sum + ((e.tickets_sold_count || 0) * (e.ticket_price || 0)), 0)
    };

    if (loading) {
        return (
            <div className="admin-raffle-loading">
                <div className="spinner"></div>
                <p>Loading raffle data...</p>
            </div>
        );
    }

    return (
        <div className="admin-raffle">
            {/* Header */}
            <div className="admin-raffle-header">
                <div className="header-left">
                    <h1>
                        <i className="fa-solid fa-ticket"></i>
                        Raffle Management
                    </h1>
                    <p>Manage raffle events, verify payments, and run draws</p>
                </div>
                <div className="header-actions">
                    <button 
                        className="btn-settings"
                        onClick={() => setShowSettingsModal(true)}
                    >
                        <i className="fa-solid fa-gear"></i>
                        Settings
                    </button>
                    <button 
                        className="btn-create"
                        onClick={() => setShowCreateModal(true)}
                    >
                        <i className="fa-solid fa-plus"></i>
                        Create Event
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon green">
                        <i className="fa-solid fa-calendar"></i>
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats.total_events}</div>
                        <div className="stat-label">Total Events</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon orange">
                        <i className="fa-solid fa-clock"></i>
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats.active_events}</div>
                        <div className="stat-label">Active Events</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon blue">
                        <i className="fa-solid fa-trophy"></i>
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats.completed_events}</div>
                        <div className="stat-label">Completed</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon purple">
                        <i className="fa-solid fa-money-bill"></i>
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">₦{stats.total_revenue.toLocaleString()}</div>
                        <div className="stat-label">Total Revenue</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon red">
                        <i className="fa-solid fa-hourglass-half"></i>
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats.pending_payments}</div>
                        <div className="stat-label">Pending Payments</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon teal">
                        <i className="fa-solid fa-ticket"></i>
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats.total_tickets_sold.toLocaleString()}</div>
                        <div className="stat-label">Tickets Sold</div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="admin-tabs">
                <button 
                    className={`tab-btn ${activeTab === 'events' ? 'active' : ''}`}
                    onClick={() => setActiveTab('events')}
                >
                    <i className="fa-solid fa-calendar"></i>
                    Events
                    <span className="tab-count">{stats.total_events}</span>
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'payments' ? 'active' : ''}`}
                    onClick={() => setActiveTab('payments')}
                >
                    <i className="fa-solid fa-money-bill-wave"></i>
                    Pending Payments
                    {stats.pending_payments > 0 && <span className="badge">{stats.pending_payments}</span>}
                </button>
            </div>

            {/* Events Tab */}
            {activeTab === 'events' && (
                <div className="events-table-container">
                    <table className="events-table">
                        <thead>
                            <tr>
                                <th>Event</th>
                                <th>Ticket Price</th>
                                <th>Tickets Sold</th>
                                <th>Total Tickets</th>
                                <th>Draw Date</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {events.map(event => (
                                <tr key={event.id}>
                                    <td>
                                        <div className="event-name">
                                            <strong>{event.title}</strong>
                                            <small>{event.description?.substring(0, 50)}</small>
                                        </div>
                                    </td>
                                    <td>₦{Number(event.ticket_price).toLocaleString()}</td>
                                    <td>{event.tickets_sold_count || 0}</td>
                                    <td>{event.total_tickets}</td>
                                    <td>{formatDate(event.draw_date)}</td>
                                    <td>
                                        <span className={`status-badge ${event.draw_status}`}>
                                            {event.draw_status === 'upcoming' ? 'Upcoming' : 
                                             event.draw_status === 'active' ? 'Active' : 
                                             event.draw_status === 'completed' ? 'Completed' : 'Cancelled'}
                                        </span>
                                    </td>
                                    <td className="actions">
                                        {event.draw_status === 'upcoming' && (
                                            <button 
                                                className="action-btn draw"
                                                onClick={() => {
                                                    setSelectedEvent(event);
                                                    setShowDrawModal(true);
                                                }}
                                            >
                                                <i className="fa-solid fa-trophy"></i>
                                                Run Draw
                                            </button>
                                        )}
                                        <button 
                                            className="action-btn view"
                                            onClick={() => handleViewEvent(event)}
                                        >
                                            <i className="fa-solid fa-eye"></i>
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {events.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="empty-row">
                                        No events created yet
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && (
                <div className="payments-container">
                    {payments.length === 0 ? (
                        <div className="empty-state">
                            <i className="fa-solid fa-check-circle"></i>
                            <h3>No Pending Payments</h3>
                            <p>All payments have been verified</p>
                        </div>
                    ) : (
                        payments.map(payment => (
                            <div key={payment.id} className="payment-card">
                                <div className="payment-header">
                                    <div className="payment-info">
                                        <h4>{payment.full_name}</h4>
                                        <p className="payment-email">{payment.email}</p>
                                        <p className="payment-event">{payment.event_title}</p>
                                    </div>
                                    <div className="payment-amount">
                                        <strong>₦{Number(payment.amount).toLocaleString()}</strong>
                                        <span>{payment.ticket_count || payment.ticket_ids?.length || 1} tickets</span>
                                    </div>
                                </div>
                                
                                <div className="payment-details">
                                    <div className="detail-row">
                                        <span className="detail-label">Reference:</span>
                                        <span className="detail-value">{payment.reference_number || 'Not provided'}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Submitted:</span>
                                        <span className="detail-value">{formatDate(payment.created_at)}</span>
                                    </div>
                                    {payment.notes && (
                                        <div className="detail-row">
                                            <span className="detail-label">Notes:</span>
                                            <span className="detail-value">{payment.notes}</span>
                                        </div>
                                    )}
                                    {payment.proof_image && (
                                        <div className="proof-preview">
                                            <button 
                                                className="view-proof-btn"
                                                onClick={() => window.open(payment.proof_image, '_blank')}
                                            >
                                                <i className="fa-solid fa-image"></i>
                                                View Payment Proof
                                            </button>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="payment-actions">
                                    <button 
                                        className="btn-approve"
                                        onClick={() => handleVerifyPayment(payment.id, 'approve')}
                                    >
                                        <i className="fa-solid fa-check"></i>
                                        Approve Payment
                                    </button>
                                    <button 
                                        className="btn-reject"
                                        onClick={() => handleVerifyPayment(payment.id, 'reject')}
                                    >
                                        <i className="fa-solid fa-times"></i>
                                        Reject Payment
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* View Event Modal */}
            {showViewModal && selectedEvent && (
                <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
                    <div className="view-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                <i className="fa-solid fa-ticket"></i>
                                Event Details
                            </h3>
                            <button className="close-btn" onClick={() => setShowViewModal(false)}>
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="event-view-details">
                                <div className="detail-group">
                                    <label>Event Title</label>
                                    <p><strong>{selectedEvent.title}</strong></p>
                                </div>
                                
                                <div className="detail-group">
                                    <label>Description</label>
                                    <p>{selectedEvent.description || 'No description provided'}</p>
                                </div>
                                
                                <div className="detail-row-view">
                                    <div className="detail-group">
                                        <label>Ticket Price</label>
                                        <p>₦{Number(selectedEvent.ticket_price).toLocaleString()}</p>
                                    </div>
                                    <div className="detail-group">
                                        <label>Max Tickets per Member</label>
                                        <p>{selectedEvent.max_tickets_per_member || 50}</p>
                                    </div>
                                </div>
                                
                                <div className="detail-row-view">
                                    <div className="detail-group">
                                        <label>Total Tickets</label>
                                        <p>{selectedEvent.total_tickets}</p>
                                    </div>
                                    <div className="detail-group">
                                        <label>Tickets Sold</label>
                                        <p>{selectedEvent.tickets_sold_count || 0}</p>
                                    </div>
                                </div>
                                
                                <div className="detail-row-view">
                                    <div className="detail-group">
                                        <label>Available Tickets</label>
                                        <p>{selectedEvent.total_tickets - (selectedEvent.tickets_sold_count || 0)}</p>
                                    </div>
                                    <div className="detail-group">
                                        <label>Draw Date</label>
                                        <p>{formatDate(selectedEvent.draw_date)}</p>
                                    </div>
                                </div>
                                
                                <div className="detail-group">
                                    <label>Status</label>
                                    <p>
                                        <span className={`status-badge ${selectedEvent.draw_status}`}>
                                            {selectedEvent.draw_status === 'upcoming' ? 'Upcoming' : 
                                             selectedEvent.draw_status === 'active' ? 'Active' : 
                                             selectedEvent.draw_status === 'completed' ? 'Completed' : 'Cancelled'}
                                        </span>
                                    </p>
                                </div>
                                
                                <div className="prizes-section-view">
                                    <h4>Prizes</h4>
                                    {selectedEvent.prizes && selectedEvent.prizes.length > 0 ? (
                                        <div className="prizes-list-view">
                                            {selectedEvent.prizes.map((prize, idx) => (
                                                <div key={idx} className="prize-item-view">
                                                    <div className="prize-position">{prize.winning_position || idx + 1}</div>
                                                    <div className="prize-details-view">
                                                        <strong>{prize.prize_name}</strong>
                                                        {prize.prize_value > 0 && (
                                                            <span>₦{Number(prize.prize_value).toLocaleString()}</span>
                                                        )}
                                                        {prize.prize_description && (
                                                            <small>{prize.prize_description}</small>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="no-prizes">No prizes added yet</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowViewModal(false)}>
                                Close
                            </button>
                            {selectedEvent.draw_status === 'upcoming' && (
                                <button 
                                    className="btn-draw"
                                    onClick={() => {
                                        setShowViewModal(false);
                                        setShowDrawModal(true);
                                    }}
                                >
                                    <i className="fa-solid fa-trophy"></i>
                                    Run Draw
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Create Event Modal */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="create-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Create Raffle Event</h3>
                            <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Event Title *</label>
                                <input 
                                    type="text"
                                    placeholder="e.g., Easter Raffle Draw 2024"
                                    value={newEvent.title}
                                    onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Description</label>
                                <textarea 
                                    rows="3"
                                    placeholder="Describe the event and prizes"
                                    value={newEvent.description}
                                    onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                                />
                            </div>
                            
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Ticket Price (₦) *</label>
                                    <input 
                                        type="number"
                                        min="100"
                                        step="100"
                                        value={newEvent.ticket_price}
                                        onChange={(e) => setNewEvent({...newEvent, ticket_price: parseInt(e.target.value)})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Max Tickets per Member</label>
                                    <input 
                                        type="number"
                                        min="1"
                                        max="500"
                                        value={newEvent.max_tickets_per_member}
                                        onChange={(e) => setNewEvent({...newEvent, max_tickets_per_member: parseInt(e.target.value)})}
                                    />
                                </div>
                            </div>
                            
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Total Tickets Available</label>
                                    <input 
                                        type="number"
                                        min="10"
                                        max="10000"
                                        value={newEvent.total_tickets}
                                        onChange={(e) => setNewEvent({...newEvent, total_tickets: parseInt(e.target.value)})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Draw Date & Time *</label>
                                    <input 
                                        type="datetime-local"
                                        value={newEvent.draw_date}
                                        onChange={(e) => setNewEvent({...newEvent, draw_date: e.target.value})}
                                    />
                                </div>
                            </div>
                            
                            <div className="prizes-section">
                                <h4>Prizes</h4>
                                <div className="add-prize">
                                    <input 
                                        type="text"
                                        placeholder="Prize name"
                                        value={newPrize.name}
                                        onChange={(e) => setNewPrize({...newPrize, name: e.target.value})}
                                    />
                                    <input 
                                        type="number"
                                        placeholder="Value (₦)"
                                        value={newPrize.value}
                                        onChange={(e) => setNewPrize({...newPrize, value: parseInt(e.target.value)})}
                                    />
                                    <button onClick={handleAddPrize}>
                                        <i className="fa-solid fa-plus"></i>
                                        Add
                                    </button>
                                </div>
                                
                                <div className="prizes-list">
                                    {newEvent.prizes.map((prize, idx) => (
                                        <div key={prize.id} className="prize-item">
                                            <div className="prize-position">{prize.position}</div>
                                            <div className="prize-details">
                                                <strong>{prize.name}</strong>
                                                {prize.value > 0 && <span>₦{prize.value.toLocaleString()}</span>}
                                            </div>
                                            <button 
                                                className="remove-prize"
                                                onClick={() => handleRemovePrize(prize.id)}
                                            >
                                                <i className="fa-solid fa-trash"></i>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowCreateModal(false)}>
                                Cancel
                            </button>
                            <button className="btn-create" onClick={handleCreateEvent}>
                                <i className="fa-solid fa-calendar-plus"></i>
                                Create Event
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Draw Confirmation Modal */}
            {showDrawModal && selectedEvent && (
                <div className="modal-overlay" onClick={() => setShowDrawModal(false)}>
                    <div className="draw-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Run Raffle Draw</h3>
                            <button className="close-btn" onClick={() => setShowDrawModal(false)}>
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="event-details">
                                <h4>{selectedEvent.title}</h4>
                                <p><strong>Tickets Sold:</strong> {selectedEvent.tickets_sold_count || 0}</p>
                                <p><strong>Prizes:</strong> {selectedEvent.total_prizes || 0}</p>
                                <p><strong>Draw Date:</strong> {formatDate(selectedEvent.draw_date)}</p>
                            </div>
                            
                            <div className="warning-box">
                                <i className="fa-solid fa-exclamation-triangle"></i>
                                <p>This action will randomly select winners for all prizes. This cannot be undone!</p>
                            </div>
                            
                            <div className="info-box">
                                <i className="fa-solid fa-info-circle"></i>
                                <p>Winners will be notified via email automatically.</p>
                            </div>
                        </div>
                        
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowDrawModal(false)}>
                                Cancel
                            </button>
                            <button className="btn-draw" onClick={handleRunDraw}>
                                <i className="fa-solid fa-trophy"></i>
                                Run Draw
                            </button>
                        </div>
                    </div>
                </div>
            )}

           {/* Settings Modal - Professional Design */}
{showSettingsModal && (
    <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
        <div className="settings-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
                <div className="modal-header-content">
                    <div className="modal-header-icon">
                        <i className="fa-solid fa-gear"></i>
                    </div>
                    <div>
                        <h3>Raffle Settings</h3>
                        <p>Configure bank details and raffle rules</p>
                    </div>
                </div>
                <button className="close-btn" onClick={() => setShowSettingsModal(false)}>
                    <i className="fa-solid fa-times"></i>
                </button>
            </div>
            
            <div className="modal-body">
                {/* Bank Details Section */}
                <div className="settings-section">
                    <div className="section-header">
                        <i className="fa-solid fa-building-columns"></i>
                        <h4>Bank Transfer Details</h4>
                    </div>
                    <div className="settings-form">
                        <div className="form-group">
                            <label>
                                <i className="fa-solid fa-university"></i>
                                Bank Name
                            </label>
                            <input 
                                type="text"
                                value={settings.bank_name || ''}
                                onChange={(e) => setSettings({...settings, bank_name: e.target.value})}
                                placeholder="e.g., Access Bank"
                                className="form-input"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>
                                <i className="fa-solid fa-user"></i>
                                Account Name
                            </label>
                            <input 
                                type="text"
                                value={settings.bank_account_name || ''}
                                onChange={(e) => setSettings({...settings, bank_account_name: e.target.value})}
                                placeholder="e.g., NLNGRA Club"
                                className="form-input"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>
                                <i className="fa-solid fa-credit-card"></i>
                                Account Number
                            </label>
                            <input 
                                type="text"
                                value={settings.bank_account_number || ''}
                                onChange={(e) => setSettings({...settings, bank_account_number: e.target.value})}
                                placeholder="e.g., 1234567890"
                                className="form-input"
                            />
                        </div>
                    </div>
                </div>

                {/* Raffle Rules Section */}
                <div className="settings-section">
                    <div className="section-header">
                        <i className="fa-solid fa-scale-balanced"></i>
                        <h4>Raffle Rules & Terms</h4>
                    </div>
                    <div className="settings-form">
                        <div className="form-group">
                            <label>
                                <i className="fa-solid fa-file-lines"></i>
                                Raffle Rules
                            </label>
                            <textarea 
                                rows="4"
                                value={settings.raffle_rules || ''}
                                onChange={(e) => setSettings({...settings, raffle_rules: e.target.value})}
                                placeholder="Enter the raffle rules and terms..."
                                className="form-textarea"
                            />
                        </div>
                    </div>
                </div>

                {/* System Settings Section */}
                <div className="settings-section">
                    <div className="section-header">
                        <i className="fa-solid fa-sliders-h"></i>
                        <h4>System Settings</h4>
                    </div>
                    <div className="settings-options">
                        <div className="toggle-option">
                            <div className="toggle-info">
                                <i className="fa-solid fa-clock"></i>
                                <div>
                                    <strong>Enable Automatic Draw</strong>
                                    <p>Automatically run the draw when the scheduled time is reached</p>
                                </div>
                            </div>
                            <label className="toggle-switch">
                                <input 
                                    type="checkbox"
                                    checked={settings.auto_draw_enabled === '1'}
                                    onChange={(e) => setSettings({...settings, auto_draw_enabled: e.target.checked ? '1' : '0'})}
                                />
                                <span className="toggle-slider"></span>
                            </label>
                        </div>
                        
                        <div className="toggle-option">
                            <div className="toggle-info">
                                <i className="fa-solid fa-bell"></i>
                                <div>
                                    <strong>Send Winner Notifications</strong>
                                    <p>Automatically email winners when the draw is completed</p>
                                </div>
                            </div>
                            <label className="toggle-switch">
                                <input 
                                    type="checkbox"
                                    checked={settings.winner_notification === '1'}
                                    onChange={(e) => setSettings({...settings, winner_notification: e.target.checked ? '1' : '0'})}
                                />
                                <span className="toggle-slider"></span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="modal-footer">
                <button className="btn-cancel" onClick={() => setShowSettingsModal(false)}>
                    <i className="fa-solid fa-times"></i>
                    Cancel
                </button>
                <button className="btn-save" onClick={handleUpdateSettings}>
                    <i className="fa-solid fa-save"></i>
                    Save Settings
                </button>
            </div>
        </div>
    </div>
)}
            
        </div>
    );
};

export default AdminRaffle;